/* ──────────────────────────────────────────────────────────────────────
 * Patchistry — Sound system (sensory polish phase, locked 2026-05-19)
 *
 * Philosophy:
 *   - Muted by default (browser autoplay safe + premium restraint)
 *   - Opt-in via small floating toggle (bottom-right), persisted in localStorage
 *   - Respects prefers-reduced-motion (treated as "no sound" too)
 *   - Only TWO sounds initially. Restraint > fireworks. Aesop/Bottega energy.
 *
 * Sound bank (SOURCE: Mixkit, free, no-attribution license)
 *   shake → vintage camera shutter (~250ms) — fires on Shake reveal
 *   cart  → soft felt-press / classic click (~180ms) — fires on Add-to-Cart success
 *
 * Swap the URLs below when Brian picks the final Mixkit clips.
 * Constants live at the top so future-Brian (or future-Claude) finds them fast.
 * ─────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  // ── Sound bank ──────────────────────────────────────────────────────
  // TODO: replace with final Mixkit URLs once Brian auditions + picks.
  // Files should live in /assets/ for Cloudflare + speed. To self-host:
  //   1. Download MP3 from Mixkit
  //   2. Upload to Shopify theme assets (any name ending .mp3)
  //   3. Replace url here with: window.Shopify.routes.root + 'cdn/shop/files/<name>.mp3'
  //      OR (cleaner) use {{ 'patchistry-shake.mp3' | asset_url }} via a Liquid var
  var SOUNDS = {
    shake: {
      // Placeholder — vintage camera shutter
      url: 'https://assets.mixkit.co/active_storage/sfx/2073/2073-preview.mp3',
      volume: 0.35
    },
    cart: {
      // Placeholder — soft felt-press / classic click
      url: 'https://assets.mixkit.co/active_storage/sfx/2568/2568-preview.mp3',
      volume: 0.28
    }
  };

  // ── State ───────────────────────────────────────────────────────────
  var STORAGE_KEY = 'patchistry:sound:enabled';
  var prefersReducedMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var enabled = (function () {
    if (prefersReducedMotion) return false;
    try { return localStorage.getItem(STORAGE_KEY) === '1'; }
    catch (e) { return false; }
  })();

  var cache = {}; // preloaded Audio instances, keyed by sound name

  // ── Core API ────────────────────────────────────────────────────────
  function preload(name) {
    if (cache[name]) return cache[name];
    var spec = SOUNDS[name];
    if (!spec) return null;
    var a = new Audio(spec.url);
    a.preload = 'auto';
    a.volume = spec.volume != null ? spec.volume : 0.3;
    cache[name] = a;
    return a;
  }

  function play(name) {
    if (!enabled) return;
    var spec = SOUNDS[name];
    if (!spec) return;
    var a = preload(name);
    if (!a) return;
    try {
      a.currentTime = 0;
      var p = a.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () { /* silent: browser blocked autoplay or file missing */ });
      }
    } catch (e) { /* silent */ }
  }

  function setEnabled(next) {
    enabled = !!next && !prefersReducedMotion;
    try { localStorage.setItem(STORAGE_KEY, enabled ? '1' : '0'); } catch (e) {}
    updateToggleUI();
    // Preload on enable so first play is instant
    if (enabled) { Object.keys(SOUNDS).forEach(preload); }
  }

  // ── Floating toggle UI ──────────────────────────────────────────────
  var toggleEl = null;

  function buildToggle() {
    if (toggleEl) return toggleEl;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'patchistry-sound-toggle';
    btn.setAttribute('aria-label', 'Toggle sound');
    btn.setAttribute('aria-pressed', enabled ? 'true' : 'false');
    btn.innerHTML = enabled ? iconOn() : iconOff();
    // On the builder (or any page with a fixed bottom tray), lift the toggle
    // above the tray so it never overlaps the COMPLETE PURCHASE button.
    if (document.querySelector('[data-pcr-tray]')) {
      btn.classList.add('patchistry-sound-toggle--tray');
    }
    btn.addEventListener('click', function () { setEnabled(!enabled); });
    document.body.appendChild(btn);
    toggleEl = btn;
    injectStyles();
    return btn;
  }

  function updateToggleUI() {
    if (!toggleEl) return;
    toggleEl.setAttribute('aria-pressed', enabled ? 'true' : 'false');
    toggleEl.innerHTML = enabled ? iconOn() : iconOff();
  }

  function iconOn() {
    return '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H2v6h4l5 4z"/><path d="M15.5 8.5a5 5 0 0 1 0 7"/><path d="M18.5 5.5a9 9 0 0 1 0 13"/></svg>';
  }
  function iconOff() {
    return '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H2v6h4l5 4z"/><line x1="22" y1="9" x2="16" y2="15"/><line x1="16" y1="9" x2="22" y2="15"/></svg>';
  }

  function injectStyles() {
    if (document.getElementById('patchistry-sound-styles')) return;
    var s = document.createElement('style');
    s.id = 'patchistry-sound-styles';
    s.textContent =
      '.patchistry-sound-toggle{' +
        'position:fixed;bottom:18px;right:18px;z-index:9999;' +
        'width:36px;height:36px;border-radius:50%;' +
        'background:rgba(26,21,18,0.78);color:#F5EFE4;' +
        'border:1px solid rgba(245,239,228,0.18);' +
        'display:inline-flex;align-items:center;justify-content:center;' +
        'cursor:pointer;backdrop-filter:blur(8px);' +
        '-webkit-backdrop-filter:blur(8px);' +
        'transition:transform .2s ease,background .2s ease,opacity .2s ease;' +
        'opacity:.55;' +
      '}' +
      '.patchistry-sound-toggle:hover{opacity:1;transform:scale(1.05);background:rgba(26,21,18,0.92)}' +
      '.patchistry-sound-toggle[aria-pressed="true"]{color:#E8C547;opacity:.9}' +
      // Lifted above the fixed tray on builder pages so it never covers the CTA
      '.patchistry-sound-toggle--tray{bottom:96px}' +
      '@media (prefers-reduced-motion: reduce){.patchistry-sound-toggle{transition:none}}' +
      '@media (max-width:749px){.patchistry-sound-toggle{bottom:14px;right:14px;width:32px;height:32px}' +
      '.patchistry-sound-toggle--tray{bottom:164px}}';
    document.head.appendChild(s);
  }

  // ── Event wiring ────────────────────────────────────────────────────
  // Shake reveal — fired by the Shake-for-a-Build flow.
  // Implementation: listen for a custom event on document, OR observe the
  // shake-reveal modal becoming visible. We support BOTH so the JS that
  // owns Shake doesn't have to know about us.
  function wireShake() {
    document.addEventListener('patchistry:shake:reveal', function () { play('shake'); });
    // Fallback: watch for the reveal modal class being added
    var mo = new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        if (m.type !== 'attributes' || m.attributeName !== 'class') return;
        var el = m.target;
        if (!(el instanceof HTMLElement)) return;
        if (el.classList && el.classList.contains('shake-reveal--visible')) play('shake');
      });
    });
    // Defer attaching observer until a reveal element exists
    var attach = function () {
      var nodes = document.querySelectorAll('[data-shake-reveal], .shake-reveal');
      nodes.forEach(function (n) { mo.observe(n, { attributes: true }); });
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', attach);
    } else { attach(); }
  }

  // Cart-add success — fired by builder tray / cart-add flow.
  function wireCart() {
    document.addEventListener('patchistry:cart:added', function () { play('cart'); });
    // Fallback: hook fetch('/cart/add') so any path that uses the Shopify
    // AJAX cart API gets sound for free.
    if (window.fetch) {
      var origFetch = window.fetch;
      window.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var isAdd = /\/cart\/add(\.js)?(\?|$)/.test(url);
        var p = origFetch.apply(this, arguments);
        if (isAdd) {
          p.then(function (res) { if (res && res.ok) play('cart'); }).catch(function(){});
        }
        return p;
      };
    }
  }

  // ── Public API ──────────────────────────────────────────────────────
  window.Patchistry = window.Patchistry || {};
  window.Patchistry.Sound = {
    play: play,
    enable: function () { setEnabled(true); },
    disable: function () { setEnabled(false); },
    isEnabled: function () { return enabled; },
    bank: SOUNDS
  };

  // ── Boot ────────────────────────────────────────────────────────────
  // Sound toggle stays hidden until the user actually interacts with the page.
  // Triggers: first scroll, first click/tap, or first key press — whichever lands first.
  function deferredBuildToggle() {
    if (toggleEl) return;
    buildToggle();
  }
  function boot() {
    wireShake();
    wireCart();
    var fired = false;
    var trigger = function () {
      if (fired) return;
      fired = true;
      deferredBuildToggle();
      window.removeEventListener('scroll', trigger);
      window.removeEventListener('click', trigger);
      window.removeEventListener('touchstart', trigger);
      window.removeEventListener('keydown', trigger);
    };
    window.addEventListener('scroll', trigger, { passive: true, once: true });
    window.addEventListener('click', trigger, { once: true });
    window.addEventListener('touchstart', trigger, { passive: true, once: true });
    window.addEventListener('keydown', trigger, { once: true });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else { boot(); }
})();
